import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import LocalMallOutlinedIcon from '@mui/icons-material/LocalMallOutlined';
import Header from '../../components/header/Header';
import Footer from '../../components/footer/Footer';
import MenuItemWithBadges from '../../components/menuitemswithbadges/MenuItemsWithBadges';
import MenuItemWithoutBadges from '../../components/menuitemwithoutbadges/MenuItemsWithoutBadges';
import styles from '../../components/categories/Categories.module.scss';
import warning from '../../../assets/warning.svg';
import { Loader } from '../../components/loader/Loader';
import { IItem, ISchedulesCategory } from './models/menuItemModels';
import MenuItemsPageContext, { IMenuItemContext } from '../../Contexts/MenuItemsPageContext/MenuItemsPageContext';
import ViewTicketDetailPageContext, {
	IViewTicketDetailPageContext,
} from '../../Contexts/ViewTicketPageContext/ViewTicketPageContext';
import AppContext, { IAppContext } from '../../Contexts/AppContext/AppContext';
import Toast from '../../components/toast/Toast';
import MenuDropdown from '../../components/menudropdown/MenuDropdown';
import { getSelectedMenuItemIndex } from '../../utils/menuItemUtils';
import homeStyles from './Home.module.scss';
import ErrorModal from '../../components/errormodal/ErrorModal';
import {
	CATEGORY_NAV_LENGTH,
	FIRST_CATEGORY_NAVIGATION_ALIGN,
	MENU_DROPDOWN_YLENGTH,
	MENU_NAVIGATION_MARGIN,
	MENU_SCROLL_MODIFIER_BUFFER_OFFSET,
	MENU_SCROLL_MODIFIER_OFFSET,
	SECTION_BUFFER_OFFSET,
	SECTION_PADDING,
} from '../../constants/constants';
import { isValidGuid } from '../../utils/homeUtils';
import * as labels from '../orderDetails/labels';
import PreAuthPageContext, { IPreAuthItemContext } from '../../Contexts/PreAuthPageContext/PreAuthPageContext';
import usePreAuthScripts from '../../hooks/usePreAuthScripts';
import OrderConfirmPageContext, {
	IOrderConfirmContext,
} from '../../Contexts/OrderConfirmPageContext/OrderConfirmPageContext';
import { getCurrencySymbol } from '../../components/helper/common';
import { Button, Typography } from '@mui/material';
import Arrow from '../../../assets/arrow.svg';
import LoaderIcon from '../../components/loaderIcon/LoaderIcon';
import { useFetchSession } from '../../hooks/useFetchSession';
import { useTranslation } from '../../hooks/useTranslation';
import { RouteConfig } from '../../routeConfig/routeConfig';

const Home: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [showLoader, setShowLoader] = useState<boolean>(true);
	const [selectedCategory, setSelectedCategory] = useState<string>();
	const [isPendingItemInCart, setIsPendingItemInCart] = useState<boolean>(false);
	const [totalPriceInCart, setTotalPriceInCart] = useState<number>(0);
	const [categoryNavXPosition, setCategoryNavXPosition] = useState<number>(MENU_NAVIGATION_MARGIN);
	const [isGuidValid, setIsGuidValid] = useState<boolean>(true);
	const [isMenuOnlyMode, setIsMenuOnlyMode] = useState<boolean>(false);
	const [isPreAuthEnabled, setIsPreAuthEnabled] = useState<boolean>(false);
	const [isSessionFlowRedirect, setIsSessionFlowRedirect] = useState<boolean>(false);
	const [payView, setPayView] = useState<boolean>(false);
	const [showToastError, setShowToastError] = useState<boolean>(false);
	const [showPayLoader, setShowPayLoader] = useState<boolean>(false);
	const {
		getTicket,
		isCheckSessionValid,
		setIsCheckSessionValid,
		isCheckSessionLoading,
		ticketError,
		setTicketError,
		shouldRedirectToViewCheck,
		ticketDetails,
	} = useContext(ViewTicketDetailPageContext) as IViewTicketDetailPageContext;
	const { getQRPayHost, payRedirectError } = useContext(OrderConfirmPageContext) as IOrderConfirmContext;

	const {
		merchantData,
		scheduleCategories,
		getMenuDetail,
		menuListSchedule,
		menuName,
		setMenuName,
		menuItemDetail,
		setScheduleCategories,
		startTime,
		setStartTime,
		endTime,
		setEndTime
	} = useContext(MenuItemsPageContext) as IMenuItemContext;
	usePreAuthScripts(isPreAuthEnabled, true, t);
	const {
		isValidSession,
		checkSessionData,
		toastMessage,
		updateToastMessage,
		itemScrollPosition,
		setItemScrollPosition,
		showLoaderIcon,
		sessionErrorStatus,
		setSessionErrorStatus,
		setIsClientTokenSet,
		checkSessionOnLoad,
	} = useContext(AppContext) as IAppContext;
	const { isActiveSession, tableName, getTableName } = useContext(PreAuthPageContext) as IPreAuthItemContext;

	const { storeId } = useParams<{ storeId: string }>();
	const storeIdSafe = storeId ? storeId : '';
	const { search, pathname } = useLocation();
	const existingTableRef = localStorage.getItem('tableRef') || '';
	const hasTableRef = new URLSearchParams(search).has('tableRef');
	const tableRef = hasTableRef ? new URLSearchParams(search).get('tableRef') : existingTableRef;
	const merchantLogoUrl = merchantData?.logoUrl ? merchantData?.logoUrl : '';
	const existingSessionId = localStorage.getItem('storedSessionId') || '';
	const [apiErrorMessage, setApiErrorMessage] = useState({ MESSAGE: '', TITLE: '', STATUS: 0 });
	const smsCode = localStorage.getItem('clientCode') || '';
	const clientToken = localStorage.getItem('clientToken') || '';
	const isQrPayEnabled =
		merchantData?.isQRPayEnabled || localStorage.getItem('isQRPayEnabled') === 'true';

	const {
		loading,
		prevTableName,
		showErrorModal,
		shouldBlockNewTable,
		invalidSession,
	} = useFetchSession({
		storeId: storeIdSafe,
		sessionId: existingSessionId,
		clientToken,
		smsCode,
		tableRef: existingTableRef,
		newTableRef: hasTableRef ? new URLSearchParams(search).get('tableRef') : null,
	});

	if (merchantData) {
		localStorage.setItem('isQRPayEnabled', JSON.stringify(merchantData.isQRPayEnabled));
	}

	useEffect(() => {
		if (!storeId && !existingSessionId && !tableRef) return;

		checkSessionOnLoad(storeIdSafe, existingSessionId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [existingSessionId, tableRef, storeId]);

	useEffect(() => {
		const handlePageShow = (event: PageTransitionEvent) => {
			if (event.persisted) {
				window.location.reload();
			}
		};
		window.addEventListener('pageshow', handlePageShow);
		return () => window.removeEventListener('pageshow', handlePageShow);
	}, []);
	
	useEffect(() => {
		if (invalidSession) {
			localStorage.clear();
			navigate(RouteConfig.scanToStart);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [invalidSession]);

	useEffect(() => {
		if (loading) return;

		if(!shouldBlockNewTable){
			localStorage.setItem('menuPage', `${pathname}`);
		}
		if (storeId) {
			getMenuDetail(storeId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [merchantData,loading]);

	useEffect(() => {
		if (loading) return;
		const redirectToHomepage = () => {
			window.location.href = 'https://www.shift4.com/';
		};
		const updateLocalStorage = () => {
			if (merchantData) {
				if (merchantData?.isPreAuthEnabled) {
					localStorage.setItem('preAuth', 'true');
					setIsPreAuthEnabled(true);
				} else {
					localStorage.removeItem('preAuth');
				}
				if (existingSessionId && storeId) {
					if (hasTableRef && tableRef && tableRef !== existingTableRef) return;
					setIsCheckSessionValid(false);
					setShowLoader(true);
					getTicket(storeId, existingSessionId, false);
				}
			}
		};

		const handleTableRef = () => {
			if (shouldBlockNewTable) return;

			if (tableRef) {
				if (existingTableRef && existingTableRef !== tableRef) {
					localStorage.clear();
				}
				if (!isValidGuid(tableRef.toString())) {
					setIsGuidValid(false);
					return;
				}
				localStorage.setItem('tableRef', tableRef);
				navigate(pathname, { replace: true });
			} else {
				setIsGuidValid(false);
				localStorage.clear();
				navigate(RouteConfig.scanToStart, { replace: true });
			}
		};

		if (!storeId) {
			redirectToHomepage();
			return;
		}
		updateLocalStorage();
		handleTableRef();
		updateToastMessage('');
		setTicketError({});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [merchantData, existingSessionId, loading]);

	useEffect(() => {
		if (loading) return;

		if (showToastError && (payRedirectError || ticketError)) {
			const apiError = payRedirectError ? payRedirectError : ticketError;
			if (apiError && apiError.code === 'USER_SESSION_NOT_FOUND') {
				updateToastMessage(t('pages:home:error:sessionNotFound:message'));
			} else if (apiError && apiError.code === 'USER_SESSION_INVALID_TICKET') {
				updateToastMessage(t('pages:home:error:invalidTicket:message'));
			} else if (apiError && apiError.code === 'PARTIAL_PAYMENT_EXISTS') {
				updateToastMessage(t('pages:viewTicket:error:partialPayment:message'));
			} else {
				updateToastMessage(t('pages:viewTicket:error:ticketNotFound:message'));
			}
			setShowPayLoader(false);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [payRedirectError, ticketError, showToastError, loading]);

	useEffect(() => {
		if (loading) return;
		if (payView && ticketDetails?.amountPaid) {
			if (Number(ticketDetails?.amountPaid > 0)) {
				updateToastMessage(t('pages:viewTicket:error:partialPayment:message'));
				setShowPayLoader(false);
				return;
			}
			if (isCheckSessionValid && existingSessionId && storeId) {
				getQRPayHost(storeId, existingSessionId);
			}
			setPayView(false);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticketDetails, payView, isValidSession, loading]);

	useEffect(() => {
		if (loading) return;

		const itemInCart = localStorage.getItem('cartItems');
		if (itemInCart) {
			const { totalPrice, items } = JSON.parse(itemInCart);
			if (totalPrice >= 0 && items?.length > 0) {
				setTotalPriceInCart(totalPrice);
				setIsPendingItemInCart(true);
			}
		}
		if (storeId) {
			getTableName(storeId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [storeId, loading]);

	useEffect(() => {
		if (menuItemDetail) {
			setMenuName(menuName);
			setStartTime(startTime);
			setEndTime(endTime);
			if (getSelectedMenuItemIndex(menuName, menuItemDetail) !== -1) {
				const activeCategories = menuItemDetail[
					getSelectedMenuItemIndex(menuName, menuItemDetail)
				].categories.filter((category: ISchedulesCategory) => category.categoryDetails?.available);
				setScheduleCategories(activeCategories);
				if (activeCategories.length > 0) {
					setSelectedCategory(activeCategories[0].categoryRef);
				}
			} else {
				setScheduleCategories([]);
				setSelectedCategory('');
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [menuItemDetail]);

	// show loader while fetching data from merchant and menu endpoints
	// TODO: refactor the code while working on QOH API calls
	useEffect(() => {
		if (loading) return;

		if (merchantData && menuListSchedule) {
			localStorage.setItem('merchantName', merchantData.name);
			document.title = merchantData.name ? `${merchantData.name} - SkyTab QR` : `SkyTab QR`;

			if (!existingSessionId) {
				setShowLoader(false);
				return;
			}

			if (shouldRedirectToViewCheck) {
				navigate(`/${storeId}/viewTicketDetail`, {
					state: { storeId },
				});
			}

			if (!isCheckSessionLoading) {
				if (isCheckSessionValid && isActiveSession) {
					navigate(`/${storeId}/OrderConfirmView`, {
						state: { orderPlaced: shouldBlockNewTable },
					});
				} else {
					setShowLoader(false);
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		merchantData,
		menuListSchedule,
		showLoader,
		isCheckSessionLoading,
		isCheckSessionValid,
		isActiveSession,
		shouldRedirectToViewCheck,
		loading
	]);

	useEffect(() => {
		if (loading) return;
		if (!isCheckSessionValid && ticketError?.status && !showToastError) {
			if ([404, 400, 500].includes(ticketError.status)) {
				setApiErrorMessage(labels.getPreAuthApiError(t, ticketError.status));
			}
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCheckSessionValid, ticketError, isPreAuthEnabled, showToastError, loading, t]);

	useEffect(() => {
		if (loading) return;

		if (isSessionFlowRedirect && !showLoaderIcon) {

			if (isValidSession) {
				storeOrderDetails();
				navigate(`/${storeId}/orderDetail`, {
					state: { storeId },
				});
			} else if (sessionErrorStatus) {
				if ([404, 400, 500].includes(sessionErrorStatus)) {
					setApiErrorMessage(labels.getPreAuthApiError(t, sessionErrorStatus));
				}
			}
			else {
				navigate(`/${storeId}/startSession`, {
					state: { storeId },
				});
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isValidSession, isSessionFlowRedirect, showLoaderIcon, loading]);

	useEffect(() => {
		window.scrollTo(0, itemScrollPosition);
		window.addEventListener('scroll', scrollHighlight);
		return () => {
			window.removeEventListener('scroll', scrollHighlight);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scheduleCategories]);

	useEffect(() => {
		const selectedItem = selectedCategory;
		//Horizontal scroll when the hidden Nav heading got highlighted
		const navigationCategory = document.getElementById(`nav-${selectedItem}`);
		if (selectedItem && navigationCategory) {
			const navigationCategoryHorizontalPosition = navigationCategory.getBoundingClientRect().x;
			const navigationCategoryWidth = navigationCategory.getBoundingClientRect().width;
			if (navigationCategoryHorizontalPosition && navigationCategoryWidth) {
				const categoryScrollLeftX =
					navigationCategoryHorizontalPosition +
					navigationCategoryWidth +
					categoryNavXPosition -
					screen.width;
				if (categoryScrollLeftX > 0 || categoryScrollLeftX < FIRST_CATEGORY_NAVIGATION_ALIGN) {
					const menuHeader = document.getElementById('navMenuHeader');
					if (menuHeader) {
						menuHeader.scrollLeft = categoryScrollLeftX;
					}
				}
				if (categoryScrollLeftX > 0) {
					setCategoryNavXPosition(categoryScrollLeftX + MENU_NAVIGATION_MARGIN);
				}
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedCategory]);

	/**
	 * This below function scroll the page to selected category on click. Also set the clicked category
	 * for style changes
	 * @param {string} categoryRef - clicked category ref.
	 */
	const handleCategoryClick = (categoryRef: string) => {
		const ScrollPos = document.getElementById('scrollSpy');
		const headerLogoElement = document.getElementById('headerLogo')?.offsetHeight
			? document.getElementById('headerLogo')?.offsetHeight
			: document.getElementsByTagName('header')[0].offsetHeight;
		const sectionTop = document.getElementById('scrollSpy')?.offsetTop;
		const mainHeader = document.getElementById('mainHeader')?.offsetHeight;

		const categoryRefElement = document.getElementById(categoryRef);
		if (categoryRef && sectionTop && ScrollPos && categoryRefElement && mainHeader) {
			const Ylength =
				headerLogoElement && headerLogoElement + MENU_DROPDOWN_YLENGTH + CATEGORY_NAV_LENGTH - SECTION_PADDING;
			if (Ylength && headerLogoElement)
				window.scrollTo({
					top: categoryRefElement.offsetTop - sectionTop + (merchantLogoUrl ? MENU_SCROLL_MODIFIER_OFFSET : 0),
					behavior: 'smooth',
				});
		}
	};

	/**
	 * This below function select the menu schedule from the dropdown list
	 * @param {string} scheduleName - selected schedule name.
	 */
	const selectMenuSchedule = (scheduleName: string, scheduleStartTime: string, scheduleEndTime: string) => {
		setMenuName(scheduleName);
		setStartTime(scheduleStartTime);
		setEndTime(scheduleEndTime);
		const activeCategories = menuItemDetail[
			getSelectedMenuItemIndex(scheduleName, menuItemDetail)
		].categories.filter((category: ISchedulesCategory) => category.categoryDetails?.available);
		setScheduleCategories(activeCategories);
		if (activeCategories?.length > 0) {
			setSelectedCategory(activeCategories[0].categoryRef);
		}
		window.scrollTo(0, 0);
	};

	/**
	 * This below function route to item detail page on click of respective item
	 */
	const handleItemClick = (itemRef: string) => {
		if (!tableRef) {
			localStorage.clear();
			navigate(RouteConfig.scanToStart);
			return;
		}
		setItemScrollPosition(window.pageYOffset);
		if (merchantData?.isDineInEnabled && !isMenuOnlyMode) {
			navigate(`/${storeId}/itemOrderPage/${itemRef}`);
		}
		if (isMenuOnlyMode) {
			updateToastMessage(t('pages:home:error:invalidGuid:message'));
		}
	};

	const handleErrorClick = () => {
		localStorage.clear();
		setIsGuidValid(true);
		setIsMenuOnlyMode(true);
	};

	const handleSessionErrorClick = () => {
		localStorage.removeItem('_grecaptcha');
		navigate(0);
		setSessionErrorStatus(0);
		setIsClientTokenSet(false);
	};

	const handleTableChangeClick = () => {
		const prevMenuPage = localStorage.getItem('menuPage');
		if(prevMenuPage) {
			navigate(prevMenuPage);
			setTimeout(() => window.location.reload(), 0);
		} 
	}

	const storeOrderDetails = () => {
		const itemInCart = localStorage.getItem('cartItems');
		const orderDetail = {
			shopName: merchantData?.name,
			table: tableRef,
			order: itemInCart && JSON.parse(itemInCart),
		};
		if (merchantData) {
			localStorage.setItem('merchantName', merchantData.name);
		}
		localStorage.setItem('orderDetail', JSON.stringify(orderDetail));
	};

	const handlePlaceOrder = () => {
		if (!tableRef) {
			localStorage.clear();
			navigate(RouteConfig.scanToStart);
			return;
		}
		setIsSessionFlowRedirect(true);
		if (isPreAuthEnabled) {
			storeOrderDetails();
			setIsCheckSessionValid(false);
			navigate(`/${storeId}/orderDetail`, {
				state: {
					storeId,
				},
			});
			return;
		}

		if (tableRef && existingSessionId && storeId) {
			checkSessionData(storeId, existingSessionId, smsCode, tableRef);
		}
	};

	const handleViewCheckClick = () => {
		setShowToastError(true);
		if (existingSessionId && storeId) {
			getTicket(storeId, existingSessionId, true);
		}
	};

	// tracks scroll position when user scroll and highlight category.
	const scrollHighlight = () => {
		const scrollPos = document.documentElement.scrollTop;
		let currentSection = 0;
		scheduleCategories?.map((section: ISchedulesCategory, index: number) => {
			const element = document.getElementById(section.categoryRef);
			const sectionTop = element?.offsetTop;
			const sectionHeight = element?.clientHeight;
			if (
				sectionTop &&
				sectionHeight &&
				scrollPos &&
				scrollPos >= sectionTop + sectionHeight - MENU_SCROLL_MODIFIER_BUFFER_OFFSET
			) {
				currentSection = index + 1;
			}
		});
		setSelectedCategory(scheduleCategories?.[currentSection]?.categoryRef);
	};

	if (!isGuidValid) {
		return (
			<ErrorModal
				handleDismiss={handleErrorClick}
				typeOfError={t('pages:home:error:invalidGuid:title')}
				errorMsg={t('pages:home:error:invalidGuid:message')}
				showButton={false}
			/>
		);
	}

	if (apiErrorMessage.MESSAGE?.length > 0) {
		return (
			<ErrorModal
				handleDismiss={handleSessionErrorClick}
				typeOfError={apiErrorMessage.TITLE}
				errorMsg={apiErrorMessage.MESSAGE}
				showButton={true}
			/>
		);
	}

	if (showErrorModal) {
		return (
			<ErrorModal
				handleDismiss={handleTableChangeClick}
				typeOfError={t('pages:home:error:tableConflict:title')}
				errorMsg={t('pages:home:error:tableConflict:message', { prevTableName })}
				showButton={true}
			/>
		);
	}

	const MenuFooter = () => {
		const hasMenus = (menuListSchedule?.length ?? 0) > 0;
		const showReviewPlaceOrderBtn =
			isPendingItemInCart &&
			!isMenuOnlyMode &&
			merchantData?.isDineInEnabled &&
			!showLoader && hasMenus;
		const balanceValue = ticketDetails?.amountDue || 0;
		const payLabel = `${merchantData && getCurrencySymbol(merchantData?.currency)}${balanceValue}`;

		return (
			<>
				{showReviewPlaceOrderBtn && (
					<Footer
						externalStyle={''}
						id={t('page:home:reviewOrder:text')}
						labelText={t('page:home:reviewOrder:text')}
						itemPrice={totalPriceInCart}
						showQuantityDetail={false}
						addToOrderInner={handlePlaceOrder}
						loaderStatus={showLoaderIcon}
					/>
				)}
				{isCheckSessionValid && !isPendingItemInCart && isQrPayEnabled && (
					<div className={homeStyles.viewdiv}>
						<Button onClick={() => navigate(`/${storeId}/viewOpenTab`)} className={homeStyles.viewTab}>
							<div>
								<Typography variant="body1" className={homeStyles.viewTabhead}>
									{showPayLoader && <LoaderIcon />}{t('page:home:viewOpenTab:text')}
								</Typography>
							</div>
							<div className={homeStyles.openTab}>
								<Typography variant="body2" className={homeStyles.pricetext}>{payLabel}</Typography>
								<img src={Arrow} className={""} alt="arrowIcon"></img>
							</div>
						</Button>
					</div>
				)}
			</>
		);
	};

	return (
		<>
			{showLoader ? (
				<Loader />
			) : (
				<>
					<div className={homeStyles.mainHeader} id="mainHeader">
						<Typography className={homeStyles.tableNumber}>
							{t('page:home:tableName:text', { tableName })}
						</Typography>
						{isCheckSessionValid &&
							<LocalMallOutlinedIcon className={homeStyles.localMallIcon} onClick={handleViewCheckClick} />
						}
					</div>
					{merchantData && (
						<Header
							id="headerLogo"
							restaurantAddress={merchantData.address}
							restaurantContact={merchantData.phone}
							restaurantInfo={merchantData.name}
							restaurantLogoImage={merchantLogoUrl}
							restaurantImage={merchantLogoUrl}
						/>
					)}
					{merchantData?.isDineInEnabled && menuListSchedule?.length ? (
						<>
							<div className={homeStyles.menuBlock}>
								<MenuDropdown
									options={menuListSchedule}
									changeSchedule={selectMenuSchedule}
									menuName={menuName}
									menuStartTime={startTime}
									menuEndTime={endTime}
								/>
							</div>
							<nav className={styles.categoriesNav}>
								<ul id="navMenuHeader" className={styles.scrollingNav}>
									{scheduleCategories?.map((category: ISchedulesCategory) => (
										<button
											key={category.categoryRef}
											name={category.categoryRef}
											id={`nav-${category.categoryRef}`}
											className={
												category.categoryRef === selectedCategory
													? styles.active
													: styles.categoryName
											}
											onClick={() => handleCategoryClick(category.categoryRef)}
										>
											{category.categoryDetails?.name}
										</button>
									))}
								</ul>
							</nav>
							<section
								id="scrollSpy"
								className={homeStyles.scrollSpy}
								style={{
									paddingBottom: `calc(100vh - ${SECTION_BUFFER_OFFSET}px)`,
								}}
							>
								{scheduleCategories?.map((category: ISchedulesCategory) => (
									<div id={category.categoryRef} key={category.categoryRef}>
										<div className={styles.categoryInfo}>
											<h3 className={styles.categoryName}>{category.categoryDetails?.name}</h3>
											<p className={styles.categoryDesc}>
												{category.categoryDetails?.description}
											</p>
										</div>
										{category.itemList?.map((item: IItem, index: number) => (
											<Fragment key={item.ref}>
												{[...item.customLabels, ...item.tags].length ? (
													<div
														role="button"
														onClick={() => handleItemClick(item.ref)}
														onKeyDown={() => handleItemClick(item.ref)}
														tabIndex={index}
													>
														<MenuItemWithBadges
															key={item.ref}
															badges={[...item.customLabels, ...item.tags]}
															itemDescription={item.description}
															itemImage={item.imageUrl800x800}
															itemImageSize="medium"
															itemName={item.name}
															itemPrice={item.price}
															currency={merchantData.currency}
															index={index}
															menuItemLength={category.itemList?.length || 0}
														/>
													</div>
												) : (
													<div
														role="button"
														onClick={() => handleItemClick(item.ref)}
														onKeyDown={() => handleItemClick(item.ref)}
														tabIndex={index}
													>
														<MenuItemWithoutBadges
															key={item.ref}
															itemDescription={item.description}
															itemImage={item.imageUrl800x800}
															itemImageSize={
																item?.imageUrl800x800?.length ? 'medium' : 'hideElement'
															}
															itemName={item.name}
															itemPrice={item.price}
															currency={merchantData.currency}
															index={index}
															menuItemLength={category.itemList?.length || 0}
														/>
													</div>
												)}
											</Fragment>
										))}
									</div>
								))}
							</section>
						</>
					) : (
						<div className={homeStyles.menuErrorContainer}>
							<div className={homeStyles.menuErrorIcon}>
								<img src={warning} className={homeStyles.menuErrorImg} alt="warning sign" />
							</div>
							<div className={homeStyles.menuErrorMsg}>{t('page:home:menuUnavailable:text')}</div>
						</div>
					)}
				</>
			)}
			<MenuFooter />
			{toastMessage?.length > 0 && <Toast message={toastMessage} />}
		</>
	);
};

export default Home;
